import { Slider, Typography } from "@mui/material";
import { FocusEvent, useCallback, useRef } from "react";
import { useLiveCursorTime } from "../hooks/useLiveCursorTime";

interface LiveTimelineCursorProps {
  visibleStartSeconds: number;
  visibleDuration: number;
  onSeek: (time: number) => void;
  formatTime: (seconds: number) => string;
}

export const LiveTimelineCursor = ({
  visibleStartSeconds,
  visibleDuration,
  onSeek,
  formatTime,
}: LiveTimelineCursorProps) => {
  const sliderRef = useRef<HTMLSpanElement>(null);
  const liveTime = useLiveCursorTime({
    trackRef: sliderRef,
    visibleDuration,
    visibleStartSeconds,
  });

  const displayCurrentTime = Math.max(
    0,
    Math.min(visibleDuration, liveTime - visibleStartSeconds)
  );

  const handleChange = useCallback(
    (_: Event, value: number | number[]) => {
      const sliderTime = Array.isArray(value) ? value[0] : value;
      onSeek(visibleStartSeconds + (sliderTime ?? 0));
    },
    [visibleStartSeconds, onSeek]
  );

  const preventFocus = useCallback((e: FocusEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).blur();
  }, []);

  return (
    <Slider
      ref={sliderRef}
      value={displayCurrentTime}
      max={visibleDuration || 100}
      onChange={handleChange}
      onFocus={preventFocus}
      valueLabelDisplay="auto"
      valueLabelFormat={(value) => formatTime(visibleStartSeconds + value)}
      sx={{
        "& .MuiSlider-thumb": {
          width: 3,
          height: 24,
          borderRadius: 1,
          backgroundColor: "primary.main",
          transition: "box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0 0 0 8px rgba(25, 118, 210, 0.16)",
          },
          "&.Mui-active": {
            boxShadow: "0 0 0 14px rgba(25, 118, 210, 0.16)",
          },
        },
        "& .MuiSlider-track": {
          height: 6,
          transition: "none",
        },
        "& .MuiSlider-valueLabel": {
          transform: "translateY(calc(100%)) scale(1) !important",
          opacity: 0,
          transition: "opacity 0.2s ease, transform 0.2s ease",
          "&.MuiSlider-valueLabelOpen": {
            top: "auto !important",
            bottom: "0 !important",
            transform: "translateY(calc(100% + 8px)) scale(1) !important",
            opacity: 1,
          },
          "&::before": {
            display: "none",
          },
          "& .MuiSlider-rail": {
            height: 6,
          },
        },
      }}
    />
  );
};

interface LiveTimeCaptionProps {
  visibleDuration: number;
  formatTime: (seconds: number) => string;
}

export const LiveTimeCaption = ({
  visibleDuration,
  formatTime,
}: LiveTimeCaptionProps) => {
  const liveTime = useLiveCursorTime({ visibleDuration });

  return (
    <Typography variant="caption" color="text.secondary">
      {formatTime(liveTime)}
    </Typography>
  );
};
